function facetDesktopDetailsHasActiveSelection(details) {
  if (details.querySelector('input[type="checkbox"]:checked')) return true;
  if (details.querySelector('input[type="radio"]:checked')) return true;
  const priceRange = details.querySelector('price-range');
  if (priceRange) {
    const hMin = priceRange.querySelector('.js-price-hidden-min');
    const hMax = priceRange.querySelector('.js-price-hidden-max');
    if (hMin && hMin.value.trim() !== '') return true;
    if (hMax && hMax.value.trim() !== '') return true;
  }
  return false;
}

function updateDesktopFacetSummaryActiveStates() {
  const wrapper = document.getElementById('FacetsWrapperDesktop');
  if (!wrapper) return;
  wrapper.querySelectorAll('details.facets__disclosure.js-filter').forEach((details) => {
    const summary = details.querySelector('summary.facets__summary');
    if (!summary) return;
    summary.classList.toggle('is-active', facetDesktopDetailsHasActiveSelection(details));
  });
}

function normalizeTieredLabelText(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .replace(/\s*[（(]\s*\d+\s*[）)]\s*$/, '')
    .trim();
}

function segmentKeyForTieredDetails(details) {
  // 外側の facets__disclosure もパスに含めないと、入れ子の
  // .facets-desktop-tiered__details をコンテナ直下から復元できない
  if (details.classList.contains('js-filter') && details.dataset.index) {
    return `idx:${details.dataset.index}`;
  }

  const input = details.querySelector(':scope > summary input.c-facets__checkbox');
  // カテゴリーは value が選択前後で変わり得るため data-tier-* を優先
  if (input?.dataset?.tierLevel) {
    return [
      'tier',
      input.dataset.tierLevel,
      input.dataset.tierL1 || '',
      input.dataset.tierL2 || '',
      input.dataset.tierL3 || '',
    ].join('\0');
  }

  const label = details.querySelector(':scope > summary .c-facets__label, :scope > summary .c-facets__tier-label');
  if (label) {
    const text = normalizeTieredLabelText(label.textContent);
    if (text) return `@${text}`;
  }

  if (input?.name && input.value !== undefined && input.value !== '') {
    return `${input.name}\0${normalizeTieredLabelText(input.value) || input.value}`;
  }

  const li = details.parentElement?.closest('li');
  if (li?.parentElement) {
    return `#${Array.from(li.parentElement.children).indexOf(li)}`;
  }
  return null;
}

function getTieredDetailsScopeParent(container, detailsEl) {
  let parent = detailsEl.parentElement;
  while (parent && parent !== container) {
    if (parent.tagName === 'DETAILS') return parent;
    parent = parent.parentElement;
  }
  return container;
}

function captureOpenTieredDetailsPaths(container) {
  const paths = [];
  container.querySelectorAll('details[open]').forEach((details) => {
    if (details === container) return;

    // 閉じた facets__disclosure 内に残った入れ子の open は無視する
    // （復元時に外側の facets__display まで開いてしまうのを防ぐ）
    const outerFilter = details.closest('details.js-filter');
    if (outerFilter && outerFilter !== details && !outerFilter.open) return;

    const segments = [];
    let current = details;
    while (current && current !== container) {
      if (current.tagName === 'DETAILS' && current.open) {
        const key = segmentKeyForTieredDetails(current);
        if (key) segments.unshift(key);
      }
      current = current.parentElement;
    }
    if (segments.length) paths.push(segments);
  });
  return paths;
}

function findTieredDetailsAtNextLevel(scope, segment) {
  for (const details of scope.querySelectorAll('details')) {
    if (details === scope) continue;
    if (getTieredDetailsScopeParent(scope, details) !== scope) continue;
    if (segmentKeyForTieredDetails(details) === segment) return details;
  }
  return null;
}

function restoreOpenTieredDetailsPaths(container, paths) {
  paths.forEach((segments) => {
    let scope = container;
    for (let i = 0; i < segments.length; i++) {
      const details = findTieredDetailsAtNextLevel(scope, segments[i]);
      if (!details) break;

      // 外側の facets__disclosure は openDisclosureIndices 側で復元する。
      // 閉じたままのポップアップをここで開かない。
      if (details.classList.contains('js-filter')) {
        if (!details.open) break;
        scope = details;
        continue;
      }

      details.open = true;
      scope = details;
    }
  });
}

function replaceFacetsBlockInnerHTML(target, html) {
  if (!target) return;

  const openPaths = captureOpenTieredDetailsPaths(target);
  const openDisclosureIndices = Array.from(target.querySelectorAll('details.js-filter[open]'))
    .map((details) => details.dataset.index)
    .filter(Boolean);

  target.innerHTML = html;

  openDisclosureIndices.forEach((index) => {
    const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(index) : index.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const details = target.querySelector(`details.js-filter[data-index="${escaped}"]`);
    if (details) details.open = true;
  });
  restoreOpenTieredDetailsPaths(target, openPaths);
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
      const syncDesktopSummaryActive = () => updateDesktopFacetSummaryActiveStates();
      this.querySelector('form').addEventListener('input', syncDesktopSummaryActive);
      this.querySelector('form').addEventListener('change', syncDesktopSummaryActive);
      queueMicrotask(syncDesktopSummaryActive);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static isSearchResultsPage() {
    return window.location.pathname.indexOf('/search') !== -1;
  }

  static normalizeSearchFacetParams(searchParams) {
    if (!searchParams || !FacetFiltersForm.isSearchResultsPage()) return searchParams;
    const params = new URLSearchParams(searchParams);
    params.set('type', 'product');
    return params.toString();
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.normalizeSearchFacetParams(searchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    if (!sections.length) return;

    const countContainers = document.querySelectorAll('#ProductCount');
    const countContainersDesktop = document.querySelectorAll('#ProductCountDesktop');
    const gridContainer = document.getElementById('ProductGridContainer');
    const collectionEl = gridContainer?.querySelector('.collection');
    if (collectionEl) collectionEl.classList.add('loading');
    countContainers.forEach((container) => container.classList.add('loading'));
    countContainersDesktop.forEach((container) => container.classList.add('loading'));

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static refreshLazyImages(container) {
    if (!container) return;
    const resolveUrl = (url) => {
      if (!url || typeof url !== 'string') return '';
      const t = url.trim();
      if (t.startsWith('//')) return `https:${t}`;
      return t;
    };

    container.querySelectorAll('img.lazy[data-src]').forEach((img) => {
      const dataSrc = img.getAttribute('data-src');
      if (!dataSrc) return;

      const hasSrc = img.hasAttribute('src') && img.getAttribute('src').trim() !== '';
      if (hasSrc) return;

      const url = resolveUrl(dataSrc);
      if (!url) return;

      const onDone = () => {
        img.classList.add('loaded');
      };

      img.addEventListener('load', onDone, { once: true });
      img.addEventListener('error', () => {
        img.classList.add('error');
      }, { once: true });

      const dataSrcset = img.getAttribute('data-srcset');
      if (dataSrcset) {
        img.srcset = dataSrcset;
      }
      img.src = url;
    });
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceContainer = parsedHTML.getElementById('ProductGridContainer');
    const sourceNoItems = parsedHTML.querySelector('.product-list__no-items');

    let grid = document.getElementById('ProductGridContainer');
    const noItems = document.querySelector('.product-list__no-items');

    if (sourceContainer) {
      if (grid) {
        grid.innerHTML = sourceContainer.innerHTML;
      } else if (noItems) {
        noItems.replaceWith(sourceContainer.cloneNode(true));
        grid = document.getElementById('ProductGridContainer');
      }
    } else if (sourceNoItems) {
      if (grid) {
        grid.replaceWith(sourceNoItems.cloneNode(true));
        grid = null;
      } else if (noItems) {
        noItems.replaceWith(sourceNoItems.cloneNode(true));
      }
    } else {
      return;
    }

    const updatedGrid = document.getElementById('ProductGridContainer');
    if (updatedGrid) {
      FacetFiltersForm.refreshLazyImages(updatedGrid);
      window.Tipo?.Auction?.init();
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHTML.getElementById('ProductCount');
    if (!sourceCount) return;

    const count = sourceCount.innerHTML;
    const containers = document.querySelectorAll('#ProductCount');
    const containersDesktop = document.querySelectorAll('#ProductCountDesktop');

    containers.forEach((container) => {
      container.innerHTML = count;
      container.classList.remove('loading');
    });

    containersDesktop.forEach((containerDesktop) => {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    });

    window.Tipo?.Auction?.init();
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const desktopWrapper = document.getElementById('FacetsWrapperDesktop');
    const sourceDesktop = parsedHTML.getElementById('FacetsWrapperDesktop');
    if (desktopWrapper && sourceDesktop) {
      replaceFacetsBlockInnerHTML(desktopWrapper, sourceDesktop.innerHTML);
    }

    const mobileMain = document.querySelector('#FacetFiltersFormMobile .mobile-facets__main');
    const sourceMobileMain = parsedHTML.querySelector('#FacetFiltersFormMobile .mobile-facets__main');
    if (mobileMain && sourceMobileMain) {
      replaceFacetsBlockInnerHTML(mobileMain, sourceMobileMain.innerHTML);
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    updateDesktopFacetSummaryActiveStates();

    if (typeof window.__facetsSyncTieredCategory === 'function') {
      window.__facetsSyncTieredCategory();
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const formMobile = document.getElementById('FacetFiltersFormMobile');
    const drawer = formMobile && formMobile.closest('menu-drawer');
    if (drawer && typeof drawer.bindEvents === 'function') {
      drawer.bindEvents();
    } else if (drawer && typeof customElements?.whenDefined === 'function') {
      customElements.whenDefined('menu-drawer').then(() => {
        const d = formMobile && formMobile.closest('menu-drawer');
        if (d && typeof d.bindEvents === 'function') {
          d.bindEvents();
        }
      });
    }
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSectionId() {
    const productGrid = document.getElementById('product-grid');
    if (productGrid?.dataset?.id) return productGrid.dataset.id;

    const mainFilters = document.getElementById('main-collection-filters');
    if (mainFilters?.dataset?.id) return mainFilters.dataset.id;

    const gridContainer = document.getElementById('ProductGridContainer');
    const nestedId = gridContainer?.querySelector('[data-id]');
    if (nestedId?.dataset?.id) return nestedId.dataset.id;

    return null;
  }

  static getSections() {
    const sectionId = FacetFiltersForm.getSectionId();
    if (!sectionId) return [];
    return [{ section: sectionId }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

document.addEventListener('click', (event) => {
  const opener = event.target.closest('.c-mobile-facets-open');
  if (!opener) return;
  const container = opener.closest('.facets-container');
  if (!container) return;
  const detailsEl = container.querySelector('menu-drawer > details.disclosure-has-popup');
  if (!detailsEl || detailsEl.hasAttribute('open')) return;
  const summary = detailsEl.querySelector('summary');
  if (!summary) return;
  summary.click();
});

function parseFacetPriceString(raw, usesCommaDecimals) {
  if (raw === '' || raw == null) return null;
  const s = String(raw).trim().replace(/\s/g, '');
  if (s === '') return null;
  if (usesCommaDecimals) {
    const withoutThousands = s.replace(/\./g, '');
    const normalized = withoutThousands.replace(',', '.');
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : null;
  }
  const n = parseFloat(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function parseRawHiddenValue(raw) {
  if (raw === '' || raw == null) return null;
  const n = parseFloat(String(raw));
  return Number.isFinite(n) ? n : null;
}

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.minInput = this.querySelector('.js-price-input-min');
    this.maxInput = this.querySelector('.js-price-input-max');
    this.hiddenMin = this.querySelector('.js-price-hidden-min');
    this.hiddenMax = this.querySelector('.js-price-hidden-max');
    this.rangeMin = this.querySelector('.js-price-range-min');
    this.rangeMax = this.querySelector('.js-price-range-max');
    this.rangeBar = this.querySelector('.js-price-range-bar');
    this.sliderEl = this.querySelector('.facets__price-slider');
    this.rangeMaxAbs = parseFloat(this.dataset.rangeMax) || 0;
    this.usesCommaDecimals = this.dataset.usesCommaDecimals === 'true';
    this.priceStep = parseFloat(this.dataset.rangeStep) || 1;
    this.hasDecimals = this.priceStep < 1;

    if (!this.minInput || !this.maxInput) return;

    this.onNumberChange = this.onNumberChange.bind(this);
    this.onNumberInput = this.onNumberInput.bind(this);
    this.onPriceFocus = this.onPriceFocus.bind(this);
    this.onPriceBlur = this.onPriceBlur.bind(this);
    this.onSliderPointerMove = this.onSliderPointerMove.bind(this);
    this.onSliderPointerLeave = this.onSliderPointerLeave.bind(this);
    this.onSliderTouchStartCapture = this.onSliderTouchStartCapture.bind(this);

    [this.minInput, this.maxInput].forEach((el) => {
      el.addEventListener('change', this.onNumberChange);
      el.addEventListener('input', this.onNumberInput);
      el.addEventListener('focus', this.onPriceFocus);
      el.addEventListener('blur', this.onPriceBlur);
    });

    this.syncVisibleFromHidden();
    this.applyPlaceholders();

    if (this.rangeMin && this.rangeMax && this.rangeBar && this.rangeMaxAbs > 0) {
      this.rangeMin.addEventListener('input', (e) => this.onRangeSliderInput(e));
      this.rangeMax.addEventListener('input', (e) => this.onRangeSliderInput(e));
      this.rangeMin.addEventListener('mousedown', () => this.setActiveThumb('min'));
      this.rangeMax.addEventListener('mousedown', () => this.setActiveThumb('max'));
      this.rangeMin.addEventListener('touchstart', () => this.setActiveThumb('min'), { passive: true });
      this.rangeMax.addEventListener('touchstart', () => this.setActiveThumb('max'), { passive: true });

      if (this.sliderEl) {
        this.sliderEl.addEventListener('pointermove', this.onSliderPointerMove);
        this.sliderEl.addEventListener('pointerleave', this.onSliderPointerLeave);
        this.sliderEl.addEventListener('pointerdown', this.onSliderPointerMove, { capture: true });
        this.sliderEl.addEventListener('touchstart', this.onSliderTouchStartCapture, { passive: true, capture: true });
      }

      this.syncSlidersFromInputs();
      this.updateRangeBar();
      this.applyPointerPassThroughNeutral();
    }
  }

  applyPlaceholders() {
    this.minInput.placeholder = this.formatDisplay(0);
    this.maxInput.placeholder = this.formatDisplay(this.rangeMaxAbs);
  }

  formatDisplay(n) {
    if (n === null || !Number.isFinite(n)) return '';
    if (this.usesCommaDecimals) {
      return n.toLocaleString('de-DE', {
        minimumFractionDigits: this.hasDecimals ? 2 : 0,
        maximumFractionDigits: 2,
      });
    }
    if (this.hasDecimals) {
      return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return n.toLocaleString('ja-JP', { maximumFractionDigits: 0 });
  }

  toEditableString(n) {
    if (this.usesCommaDecimals) {
      return n.toLocaleString('de-DE', {
        useGrouping: false,
        minimumFractionDigits: this.hasDecimals ? 2 : 0,
        maximumFractionDigits: 2,
      });
    }
    if (this.hasDecimals) {
      return n.toLocaleString('en-US', {
        useGrouping: false,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return String(Math.round(n));
  }

  syncVisibleFromHidden() {
    if (!this.hiddenMin || !this.hiddenMax) return;

    const minN = parseRawHiddenValue(this.hiddenMin.value);
    const maxN = parseRawHiddenValue(this.hiddenMax.value);

    this.minInput.value = minN === null ? '' : this.formatDisplay(minN);
    this.maxInput.value = maxN === null ? '' : this.formatDisplay(maxN);
  }

  syncHiddenFromVisible() {
    if (!this.hiddenMin || !this.hiddenMax) return;

    const minN = this.parseInputNumber(this.minInput);
    const maxN = this.parseInputNumber(this.maxInput);

    if (minN === null || minN === 0) {
      this.hiddenMin.value = '';
    } else {
      this.hiddenMin.value = String(minN);
    }

    if (maxN === null || maxN >= this.rangeMaxAbs - 1e-9) {
      this.hiddenMax.value = '';
    } else {
      this.hiddenMax.value = String(maxN);
    }

    this.hiddenMin.dispatchEvent(new Event('input', { bubbles: true }));
    this.hiddenMax.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onPriceFocus(event) {
    const el = event.currentTarget;
    const n = this.parseInputNumber(el);
    if (el.value === '' || n === null) return;
    el.value = this.toEditableString(n);
  }

  onPriceBlur(event) {
    this.adjustToValidValues(event.currentTarget);
    if (this.rangeMin && this.rangeMax) {
      this.syncSlidersFromInputs();
      this.updateRangeBar();
      this.applyPointerPassThroughNeutral();
    }
  }

  /**
   * 重ねた2つの range は上側だけがポインターを拾うため、
   * カーソル位置が min / max のどちらのつまみに近いかで pointer-events を切り替える。
   */
  applyPointerPassThroughRatio(ratio) {
    if (!this.rangeMin || !this.rangeMax || this.rangeMaxAbs <= 0) return;

    const minV = this.rangeMin.valueAsNumber;
    const maxV = this.rangeMax.valueAsNumber;
    const minPos = minV / this.rangeMaxAbs;
    const maxPos = maxV / this.rangeMaxAbs;
    const distMin = Math.abs(ratio - minPos);
    const distMax = Math.abs(ratio - maxPos);

    let preferMin;
    if (distMin < distMax) {
      preferMin = true;
    } else if (distMax < distMin) {
      preferMin = false;
    } else {
      const mid = (minPos + maxPos) / 2;
      preferMin = ratio <= mid;
    }

    if (preferMin) {
      this.rangeMin.style.pointerEvents = 'auto';
      this.rangeMax.style.pointerEvents = 'none';
    } else {
      this.rangeMin.style.pointerEvents = 'none';
      this.rangeMax.style.pointerEvents = 'auto';
    }
  }

  applyPointerPassThroughNeutral() {
    if (!this.rangeMin || !this.rangeMax || this.rangeMaxAbs <= 0) return;

    const minV = this.rangeMin.valueAsNumber;
    const maxV = this.rangeMax.valueAsNumber;
    const minPos = minV / this.rangeMaxAbs;
    const maxPos = maxV / this.rangeMaxAbs;
    const ratio = (minPos + maxPos) / 2;
    this.applyPointerPassThroughRatio(ratio);
  }

  updatePointerPassThroughFromClientX(clientX) {
    if (!this.sliderEl || this.rangeMaxAbs <= 0) return;

    const rect = this.sliderEl.getBoundingClientRect();
    const w = rect.width || 1;
    let ratio = (clientX - rect.left) / w;
    if (ratio < 0) ratio = 0;
    if (ratio > 1) ratio = 1;
    this.applyPointerPassThroughRatio(ratio);
  }

  onSliderPointerMove(event) {
    if (typeof event.clientX !== 'number') return;
    this.updatePointerPassThroughFromClientX(event.clientX);
  }

  onSliderTouchStartCapture(event) {
    const t = event.touches && event.touches[0];
    if (t) this.updatePointerPassThroughFromClientX(t.clientX);
  }

  onSliderPointerLeave() {
    this.applyPointerPassThroughNeutral();
  }

  setActiveThumb(which) {
    if (!this.rangeMin || !this.rangeMax) return;
    this.rangeMin.classList.toggle('is-active', which === 'min');
    this.rangeMax.classList.toggle('is-active', which === 'max');
  }

  onNumberInput() {
    this.syncHiddenFromVisible();
    if (this.rangeMin && this.rangeMax) {
      this.syncSlidersFromInputs();
      this.updateRangeBar();
      this.applyPointerPassThroughNeutral();
    }
  }

  onNumberChange(event) {
    this.adjustToValidValues(event.currentTarget);
    if (this.rangeMin && this.rangeMax) {
      this.syncSlidersFromInputs();
      this.updateRangeBar();
      this.applyPointerPassThroughNeutral();
    }
  }

  parseInputNumber(input) {
    if (!input) return null;
    return parseFacetPriceString(input.value, this.usesCommaDecimals);
  }

  syncSlidersFromInputs() {
    if (!this.rangeMin || !this.rangeMax || this.rangeMaxAbs <= 0) return;

    let minV = this.parseInputNumber(this.minInput);
    let maxV = this.parseInputNumber(this.maxInput);

    if (minV === null) minV = 0;
    if (maxV === null) maxV = this.rangeMaxAbs;

    minV = Math.max(0, Math.min(minV, this.rangeMaxAbs));
    maxV = Math.max(0, Math.min(maxV, this.rangeMaxAbs));
    if (minV > maxV) {
      const t = minV;
      minV = maxV;
      maxV = t;
    }

    this.rangeMin.value = String(minV);
    this.rangeMax.value = String(maxV);
  }

  onRangeSliderInput(event) {
    if (!this.rangeMin || !this.rangeMax) return;

    let minV = this.rangeMin.valueAsNumber;
    let maxV = this.rangeMax.valueAsNumber;

    if (minV > maxV) {
      if (event.currentTarget === this.rangeMin) {
        this.rangeMax.value = String(minV);
        maxV = minV;
      } else {
        this.rangeMin.value = String(maxV);
        minV = maxV;
      }
    }

    const atMax = maxV >= this.rangeMaxAbs - 1e-9;

    if (this.hiddenMin && this.hiddenMax) {
      if (minV === 0) {
        this.hiddenMin.value = '';
        this.minInput.value = '';
      } else {
        this.hiddenMin.value = String(minV);
        this.minInput.value = this.formatDisplay(minV);
      }
      if (atMax) {
        this.hiddenMax.value = '';
        this.maxInput.value = '';
      } else {
        this.hiddenMax.value = String(maxV);
        this.maxInput.value = this.formatDisplay(maxV);
      }
      this.hiddenMin.dispatchEvent(new Event('input', { bubbles: true }));
      this.hiddenMax.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.updateRangeBar();
    this.applyPointerPassThroughNeutral();
  }

  updateRangeBar() {
    if (!this.rangeBar || this.rangeMaxAbs <= 0) return;

    const minV = this.rangeMin ? this.rangeMin.valueAsNumber : 0;
    const maxV = this.rangeMax ? this.rangeMax.valueAsNumber : this.rangeMaxAbs;

    const leftPct = (minV / this.rangeMaxAbs) * 100;
    const widthPct = ((maxV - minV) / this.rangeMaxAbs) * 100;

    this.rangeBar.style.left = `${leftPct}%`;
    this.rangeBar.style.width = `${Math.max(0, widthPct)}%`;
  }

  adjustToValidValues(input) {
    if (!this.hiddenMin || !this.hiddenMax) return;

    if (input.value.trim() === '') {
      if (input === this.minInput) {
        this.hiddenMin.value = '';
      } else {
        this.hiddenMax.value = '';
      }
      this.hiddenMin.dispatchEvent(new Event('input', { bubbles: true }));
      this.hiddenMax.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }

    let n = this.parseInputNumber(input);
    if (n === null) return;

    const maxBound = this.rangeMaxAbs;
    const otherInput = input === this.minInput ? this.maxInput : this.minInput;
    const otherN = this.parseInputNumber(otherInput);

    if (input === this.minInput) {
      let maxLimit = maxBound;
      if (otherN !== null) maxLimit = Math.min(maxLimit, otherN);
      n = Math.max(0, Math.min(n, maxLimit));
      if (n === 0) {
        input.value = '';
        this.hiddenMin.value = '';
      } else {
        input.value = this.formatDisplay(n);
        this.hiddenMin.value = String(n);
      }
    } else {
      let minLimit = 0;
      if (otherN !== null) minLimit = Math.max(minLimit, otherN);
      n = Math.max(minLimit, Math.min(n, maxBound));
      if (n >= maxBound - 1e-9) {
        input.value = '';
        this.hiddenMax.value = '';
      } else {
        input.value = this.formatDisplay(n);
        this.hiddenMax.value = String(n);
      }
    }

    this.hiddenMin.dispatchEvent(new Event('input', { bubbles: true }));
    this.hiddenMax.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define('facet-remove', FacetRemove);

const FACETS_DISPLAY_BOTTOM_GAP = 16;

function getFacetsDisplayViewportBottom() {
  const vv = window.visualViewport;
  if (vv) return vv.offsetTop + vv.height;
  return window.innerHeight;
}

function fitFacetsDisplayToViewport(details) {
  if (!details || !details.open) return;
  const display = details.querySelector(':scope > .facets__display');
  if (!display) return;

  const isNarrow = window.matchMedia('(max-width: 719.97px)').matches;
  // モバイルでは position:unset のため form-top-inner 基準、それ以外は details 基準
  const anchor = isNarrow
    ? details.closest('.facets__form-top-inner') || details
    : details;
  const top = anchor.getBoundingClientRect().bottom + 8;
  const available = Math.floor(getFacetsDisplayViewportBottom() - top - FACETS_DISPLAY_BOTTOM_GAP);

  if (available > 0) {
    display.style.setProperty('--facets-display-max-height', `${available}px`);
  }
}

function fitAllOpenFacetsDisplays() {
  document.querySelectorAll('details.facets__disclosure.js-filter[open]').forEach((details) => {
    fitFacetsDisplayToViewport(details);
  });
}

document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement)) return;
  if (!details.classList.contains('facets__disclosure') || !details.classList.contains('js-filter')) return;

  if (details.open) {
    requestAnimationFrame(() => fitFacetsDisplayToViewport(details));
  } else {
    const display = details.querySelector(':scope > .facets__display');
    if (display) display.style.removeProperty('--facets-display-max-height');
  }
}, true);

window.addEventListener('resize', fitAllOpenFacetsDisplays);
if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', fitAllOpenFacetsDisplays);
  window.visualViewport.addEventListener('scroll', fitAllOpenFacetsDisplays);
}
